import React, { useState } from "react";
import { MapPin, Mail, Phone, Instagram, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { INSTAGRAM, INSTAGRAM_URL, whatsappLink } from "../lib/constants";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensagem enviada! Em breve entraremos em contato.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="py-20">
      <div className="container-page grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Contato</div>
          <h1 className="font-display text-5xl md:text-6xl text-forest-900 leading-[1.02]">
            Vamos conversar sobre a floresta.
          </h1>
          <div className="section-divider my-7" />
          <p className="text-forest-900/80 leading-relaxed">
            Dúvidas sobre produtos, pedidos, visitas à cooperativa, parcerias institucionais ou imprensa — nossa equipe
            está pronta para te atender.
          </p>

          <div className="mt-10 space-y-5 text-forest-900/90">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
              <div>
                <div className="font-medium">Sede</div>
                <div className="text-forest-900/70">Floresta Nacional do Tapajós — Belterra (PA), Brasil</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></div>
              <div>
                <div className="font-medium">Atendimento</div>
                <a href={whatsappLink("Olá! Vim pelo site da COOMFLONA.")} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="text-forest-700 hover:text-copaiba-700">
                  WhatsApp — Eco Loja e Cooperativa
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></div>
              <div>
                <div className="font-medium">E-mail</div>
                <div className="text-forest-900/70">contato@coomflona.com.br</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center shrink-0"><Instagram className="w-5 h-5" /></div>
              <div>
                <div className="font-medium">Instagram</div>
                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="text-forest-700 hover:text-copaiba-700">@{INSTAGRAM}</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-7 bg-white border border-cream-400 p-8 md:p-10" data-testid="contact-form">
          <h2 className="font-display text-3xl text-forest-900 mb-8">Envie uma mensagem</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Nome" name="name" value={form.name} onChange={onChange} required />
            <Field label="E-mail" name="email" type="email" value={form.email} onChange={onChange} required />
            <Field label="Telefone (opcional)" name="phone" value={form.phone} onChange={onChange} />
            <Field label="Assunto" name="subject" value={form.subject} onChange={onChange} required />
          </div>
          <label className="block mt-5">
            <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Mensagem</span>
            <textarea
              name="message"
              required
              rows={6}
              value={form.message}
              onChange={onChange}
              data-testid="contact-message-input"
              className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-4 text-forest-900 resize-none"
            />
          </label>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="btn-primary mt-6"
          >
            {sending ? "Enviando..." : (<>Enviar mensagem <Send className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">{label}</span>
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={`contact-${name}-input`}
        className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3.5 text-forest-900"
      />
    </label>
  );
}
